import type { SxProps, Theme } from '@mui/material/styles'
import { muahangKiotLike } from '../../layout/muahangKiotTokens'

export const KIOT_EXPAND_FRAME_PX = 2

/** Khoảng cách giữa hàng dữ liệu và nội dung panel — 0 để khung ôm sát full row. */
export const KIOT_EXPAND_CONTENT_BUFFER_PX = 0

/** Class `TableRow` panel expand — đồng bộ `TableList` + `KiotListExpandPanel`. */
export const KIOT_EXPAND_PANEL_CLASS = 'kiot-list-expand-panel'

/** Attribute hàng dữ liệu đang mở panel expand. */
export const KIOT_EXPAND_FRAME_ATTR = 'data-expand-frame'

/** Nền hàng + vùng overlap khi expand. */
export function kiotExpandCardBg(theme: Theme): string {
  return theme.palette.mode === 'dark' ? 'rgba(37, 99, 235, 0.1)' : '#eff6ff'
}

/** @deprecated Không dùng margin âm — viền top vẽ trên hàng dữ liệu. */
export const KIOT_EXPAND_ROW_OVERLAP_PX = 41

export function kiotExpandFrameCornerRadius(theme: Theme): number {
  const br = theme.shape.borderRadius
  if (typeof br === 'number' && Number.isFinite(br)) {
    return Math.max(2, Math.round(br / 2))
  }
  return 2
}

export type KiotExpandFrameTopSlot = 'first' | 'middle' | 'last'

/** Sticky mép trái/phải viewport khi expand — giữ viền khung khi cuộn ngang. */
export function kiotExpandViewportEdgeStickyCellSx(
  theme: Theme,
  edge: 'first' | 'last',
): Record<string, unknown> {
  return {
    [theme.breakpoints.up('md')]: {
      position: 'sticky',
      zIndex: 4,
      ...(edge === 'first' ? { left: 0 } : { right: 0 }),
    },
  }
}

/** Viền trên hàng dữ liệu khi panel expand mở — nối liền khung panel Kiot. */
export function kiotExpandFrameTopCellSx(
  theme: Theme,
  slot: KiotExpandFrameTopSlot,
  opts?: { viewportStickyLeft?: boolean; viewportStickyRight?: boolean },
): SxProps<Theme> {
  const c = theme.palette.primary.main
  const r = kiotExpandFrameCornerRadius(theme)
  const top = {
    borderTop: `${KIOT_EXPAND_FRAME_PX}px solid ${c} !important`,
    borderBottom: 'none !important',
  }
  const sticky =
    opts?.viewportStickyLeft && slot === 'first'
      ? kiotExpandViewportEdgeStickyCellSx(theme, 'first')
      : opts?.viewportStickyRight && slot === 'last'
        ? kiotExpandViewportEdgeStickyCellSx(theme, 'last')
        : {}
  if (slot === 'first') {
    return {
      ...top,
      ...sticky,
      borderLeft: `${KIOT_EXPAND_FRAME_PX}px solid ${c} !important`,
      borderTopLeftRadius: r,
      boxShadow: 'none !important',
    }
  }
  if (slot === 'last') {
    return {
      ...top,
      ...sticky,
      borderRight: `${KIOT_EXPAND_FRAME_PX}px solid ${c} !important`,
      borderTopRightRadius: r,
      boxShadow: 'none !important',
    }
  }
  return top
}

/** @deprecated Viền hàng expand dùng `KiotExpandViewportFrame` — không gắn border từng ô. */
export function kiotExpandSelectedRowSx(_theme: Theme, _cellBg?: string): Record<string, unknown> {
  return {}
}

/** Shell sticky viewport ngang — ôm nội dung, trần = phần còn lại dưới data row. */
export function kiotExpandClosedFrameShellSx(
  theme: Theme,
  isExpanded: boolean,
  panelVisibleWidthPx?: number,
  _dataRowOverlapPx?: number,
  panelMaxHeightPx?: number,
): SxProps<Theme> {
  const useViewportW = isExpanded && panelVisibleWidthPx != null && panelVisibleWidthPx > 0
  const maxH =
    panelMaxHeightPx != null && panelMaxHeightPx > 0
      ? panelMaxHeightPx
      : isExpanded
        ? 'var(--kiot-expand-panel-max-h, none)'
        : undefined

  return {
    position: isExpanded ? 'sticky' : 'static',
    left: isExpanded ? 0 : 'auto',
    // Không sticky top — data row đã sticky dưới thead
    top: 'auto',
    zIndex: isExpanded ? 2 : 'auto',
    width: useViewportW ? panelVisibleWidthPx : '100%',
    maxWidth: useViewportW ? panelVisibleWidthPx : '100%',
    minWidth: 0,
    flexShrink: 0,
    boxSizing: 'border-box',
    paddingTop: isExpanded ? `${KIOT_EXPAND_CONTENT_BUFFER_PX}px` : 0,
    height: isExpanded ? 'auto' : undefined,
    maxHeight: isExpanded ? maxH : undefined,
    display: isExpanded ? 'flex' : 'block',
    flexDirection: 'column',
    overflow: isExpanded ? 'hidden' : 'visible',
    border: 'none',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    pointerEvents: isExpanded ? 'none' : 'auto',
    '& > *': isExpanded
      ? {
          pointerEvents: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }
      : {},
  }
}

/** @deprecated Dùng `kiotExpandClosedFrameShellSx` cho khung row+expand. */
export function kiotExpandPanelShellSx(
  theme: Theme,
  isExpanded: boolean,
  panelVisibleWidthPx?: number,
  dataRowOverlapPx?: number,
  panelMaxHeightPx?: number,
): SxProps<Theme> {
  return kiotExpandClosedFrameShellSx(
    theme,
    isExpanded,
    panelVisibleWidthPx,
    dataRowOverlapPx,
    panelMaxHeightPx,
  )
}

/** Tab bar panel expand — gạch chân primary trên nền trắng. */
export const kiotExpandDetailTabsSx: SxProps<Theme> = {
  minHeight: 44,
  bgcolor: 'transparent',
  borderBottom: 'none',
  px: 0,
  flex: '1 1 auto',
  minWidth: 0,
  flexShrink: 1,
  position: 'relative',
  zIndex: 1,
  '& .MuiTab-root': {
    minHeight: 44,
    py: 0,
    px: 0,
    mr: 3,
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none',
    color: muahangKiotLike.kiotTextMuted,
    minWidth: 'auto',
    opacity: 1,
  },
  '& .MuiTab-root.Mui-selected': {
    color: muahangKiotLike.linkBlue,
    fontWeight: 600,
  },
  '& .MuiTab-root:not(.Mui-selected):hover': {
    color: muahangKiotLike.kiotTextPrimary,
  },
  '& .MuiTabs-indicator': {
    height: 2,
    bgcolor: muahangKiotLike.linkBlue,
  },
}

/** Hàng tab + nút hành động (nút căn phải). */
export const kiotExpandDetailHeaderBarSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1.5,
  minHeight: 44,
  bgcolor: '#fff',
  borderBottom: '1px solid',
  borderColor: muahangKiotLike.tableBorder,
  px: 2.5,
  flexShrink: 0,
  position: 'relative',
  zIndex: 1,
}

/** Style nút nhỏ trong expand (top/bottom). */
export const kiotExpandDetailActionButtonRulesSx: SxProps<Theme> = {
  '& .MuiButton-root': {
    minHeight: 28,
    height: 28,
    maxHeight: 28,
    px: 1.25,
    py: 0,
    minWidth: 0,
    fontSize: '0.8125rem',
    fontWeight: 600,
    lineHeight: 1.2,
  },
  '& .MuiButton-startIcon': {
    marginLeft: 0,
    marginRight: 0.4,
    '& > *:nth-of-type(1)': { fontSize: '0.95rem' },
  },
  '& .MuiButton-endIcon': {
    marginLeft: 0.4,
    marginRight: 0,
    '& > *:nth-of-type(1)': { fontSize: '0.95rem' },
  },
}

/** Nút chọn cột trên hàng tab expand — cùng cao 28px với nút Export. */
export const kiotExpandColumnPickerTriggerSx = {
  minHeight: '28px !important',
  height: '28px !important',
  minWidth: 36,
  width: 'auto',
  px: 0.5,
  py: 0,
} as const

export const kiotExpandDetailHeaderActionsSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: 0.75,
  flex: '0 1 auto',
  maxWidth: '100%',
  py: 0.5,
  ...kiotExpandDetailActionButtonRulesSx,
}

export const kiotExpandDetailTabPanelSx: SxProps<Theme> = {
  bgcolor: '#fff',
  flex: '1 1 auto',
  minHeight: 0,
  overflowX: 'hidden',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
}

/** Padding quanh nội dung tab expand (bảng lồng / form). */
export const kiotExpandDetailPanelPadSx: SxProps<Theme> = {
  px: 2.5,
  py: 1.75,
  bgcolor: '#fff',
}

/** Khung outlined level-2 cho bảng lồng trong panel expand. */
export const kiotExpandNestedTableWrapSx: SxProps<Theme> = {
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  overflow: 'auto',
  bgcolor: '#fff',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  '& .MuiTableContainer-root, & .ant-table': {
    maxWidth: '100%',
  },
}

export const kiotExpandDetailGeneralPanelSx: SxProps<Theme> = {
  px: 2.5,
  py: 1.5,
}

export const kiotExpandDetailMetaRowSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 0.75,
  pb: 1.5,
  mb: 1.5,
  borderBottom: `1px solid ${muahangKiotLike.tableBorder}`,
}

export const kiotExpandDetailInfoGridSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
  columnGap: 3,
  rowGap: 2.25,
}

/** Grid field compact — label + value cùng dòng, nhiều cột hơn. */
export const kiotExpandDetailInfoGridCompactSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))',
    lg: 'repeat(4, minmax(0, 1fr))',
  },
  columnGap: { xs: 1.5, sm: 2 },
  rowGap: 0.65,
}

export const kiotExpandDetailStatCardSx: SxProps<Theme> = {
  px: 1.75,
  py: 1.25,
  borderRadius: `${muahangKiotLike.filterSidebarCardRadiusPx}px`,
  border: muahangKiotLike.filterSidebarSectionBorder,
  bgcolor: '#fff',
}

export const kiotExpandDetailEmptyHintSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  py: 3,
  px: 2,
  borderRadius: `${muahangKiotLike.filterSidebarCardRadiusPx}px`,
  border: '1px dashed',
  borderColor: 'rgba(0, 0, 0, 0.12)',
  bgcolor: '#fafafa',
}

export const kiotExpandDetailFooterSx: SxProps<Theme> = {
  bgcolor: '#fff',
  borderTop: `1px solid ${muahangKiotLike.tableBorder}`,
  py: 0.75,
  px: 2.5,
  ...kiotExpandDetailActionButtonRulesSx,
}

/** @deprecated */
export const kiotExpandDetailProfileHeaderSx: SxProps<Theme> = kiotExpandDetailMetaRowSx

/** @deprecated */
export const kiotExpandDetailBodySx: SxProps<Theme> = kiotExpandDetailMetaRowSx

/** @deprecated */
export const kiotExpandDetailDebtSummarySx: SxProps<Theme> = kiotExpandDetailStatCardSx
